import React, { useState, useRef, useEffect } from "react";
import "./UploadDocument.scss";
import { BASE_URL } from "config/config";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

const UploadDocument = ({ 
  onUploadSuccess, 
  showProgressPopup, 
  setShowProgressPopup, 
  showSuccessPopup, 
  setShowSuccessPopup, 
  uploadProgress, 
  setUploadProgress 
}) => {
  const currentUser = useSelector((state) => state.auth.user);
  const companyId = currentUser?.companyId?._id;
  console.log(companyId);
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState(t("signature.nomFichier"));
  const [pageCount, setPageCount] = useState(0);
  const [uploadStatus, setUploadStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const fileInputRef = useRef(null);

  // Cleanup PDF URL on component unmount
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  // Function to get PDF page count
  const getPDFPageCount = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = function(e) {
        const typedArray = new Uint8Array(e.target.result);
        const pdfData = typedArray;
        
        // Simple regex to count pages in PDF
        const pdfString = String.fromCharCode.apply(null, pdfData);
        const pageMatches = pdfString.match(/\/Page\W/g);
        const pageCount = pageMatches ? pageMatches.length : 1;
        resolve(pageCount);
      };
      reader.readAsArrayBuffer(file);
    });
  };

  // Handle file processing
  const processFile = async (file) => {
    if (!file) {
      // Clean up previous PDF URL
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }
      setSelectedFile(null);
      setFileName(t("signature.nomFichier"));
      setPageCount(0);
      setUploadStatus(t("signature.selectionne"));
      return;
    }

    if (
      file.type !== "application/pdf" &&
      !file.name.toLowerCase().endsWith(".pdf")
    ) {
      // Clean up previous PDF URL
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }
      setSelectedFile(null);
      setFileName(t("signature.nomFichier"));
      setPageCount(0);
      setUploadStatus(t("signature.pdfUnique"));
      return;
    }

    // Get page count
    try {
      const pages = await getPDFPageCount(file);
      setPageCount(pages);
    } catch (error) {
      console.error("Error getting page count:", error);
      setPageCount(1); // Default to 1 if we can't determine
    }

    // ✅ Fichier valide
    setSelectedFile(file);
    setFileName(file.name);
    setUploadStatus("");
    
    // Create URL for PDF viewer
    const url = URL.createObjectURL(file);
    setPdfUrl(url);
  };

  // Gérer le choix du fichier
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    processFile(file);
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    processFile(file);
  };

  const handleUploadAreaClick = () => {
    fileInputRef.current?.click();
  };

  // Envoyer le fichier au backend
  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadStatus(t("signature.aucunFichier"));
      return;
    }

    setLoading(true);
    setUploadStatus("");
    setShowProgressPopup(true);
    setUploadProgress(0);

    // Simulate progress
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return prev;
        }
        return prev + Math.random() * 15;
      });
    }, 200);

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("companyId", companyId);

    try {
      const res = await fetch(`${BASE_URL}documentsFile/upload/signature`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) throw new Error(t("signature.erreurUpload"));

      const data = await res.json();
      
      // Complete progress
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      // Wait a moment then show success
      setTimeout(() => {
        setShowProgressPopup(false);
        setShowSuccessPopup(true);
        // setUploadStatus(`✅ Fichier envoyé avec succès. ID : ${data.documentId}`);
        if (onUploadSuccess) {
          onUploadSuccess(data.documentId.toString());
        }
      }, 500);
      
    } catch (err) {
      clearInterval(progressInterval);
      setShowProgressPopup(false);
      setUploadStatus(t("signature.erruerEnvoi"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="upload-document-container">
      {/* PDF Preview Section */}
      <div className="pdf-preview-sectionSign">
        {selectedFile && pdfUrl ? (
          <div className="pdf-preview-container">
            <div className="pdf-header">
              <div className="pdf-info">
                <div className="pdf-name">{fileName}</div>
                <div className="pdf-pages">{pageCount} pages</div>
              </div>
              <div className="pdf-options">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="3" r="1" fill="#666"/>
                  <circle cx="8" cy="8" r="1" fill="#666"/>
                  <circle cx="8" cy="13" r="1" fill="#666"/>
                </svg>
              </div>
            </div>
            <div className="pdf-viewer">
              <iframe
                src={pdfUrl}
                type="application/pdf"
                width="100%"
                height="100%"
                title="PDF Preview"
                className="pdf-iframe"
              />
            </div>
          </div>
        ) : (
          <div className="no-file-preview">
            <div className="no-file-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="#ccc" strokeWidth="2" fill="none"/>
                <path d="M14 2V8H20" stroke="#ccc" strokeWidth="2" fill="none"/>
                <path d="M16 13H8" stroke="#ccc" strokeWidth="2"/>
                <path d="M16 17H8" stroke="#ccc" strokeWidth="2"/>
                <path d="M10 9H8" stroke="#ccc" strokeWidth="2"/>
              </svg>
            </div>
            <div className="no-file-text">Aucun document chargé</div>
          </div>
        )}
      </div>

      {/* Upload Area Section */}
      <div className="upload-area-section">
        <div 
          className={`upload-area ${isDragOver ? 'drag-over' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleUploadAreaClick}
        >
          <div className="upload-icon">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <path d="M24 4L24 44M4 24L44 24" stroke="#0f84ff" strokeWidth="3" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="upload-text">Déposer les fichiers ici ou</div>
          <div className="upload-actions">
            <label htmlFor="realFile" className="upload-button">
              Charger
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 6L8 10L12 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </label>
            <input
              ref={fileInputRef}
              type="file"
              id="realFile"
              className="file-input"
              onChange={handleFileChange}
              accept=".pdf"
            />
          </div>
        </div>

        {/* Submit Button - Now below upload area */}
        {selectedFile && (
          <div className="upload-actions-section">
            <button
              onClick={handleUpload}
              disabled={loading}
              className="submit-upload-button"
            >
              {loading ? "Envoi..." : "Télécharger le document"}
            </button>
          </div>
        )}

        {/* Upload Status */}
        {/* {uploadStatus && (
          <div className="upload-status">
            <p className={uploadStatus.includes("✅") ? "success" : "error"}>
              {uploadStatus}
            </p>
          </div>
        )} */}
      </div>

    </div>
  );
};

export default UploadDocument;
